import React, { useState, useEffect, useRef, useMemo } from "react";
import styled, { keyframes } from "styled-components";
import { motion } from "framer-motion";
import { useHistory } from "react-router-dom";
import { cachedGet, readCache } from "../api";
import HHLogo from "../sidebar/logo.svg";
import BottomNavBar from "./BottomNavBar";
import { useAmazonContext } from "../../Contexts/AmazonContext";
import LottieAnimation from "../LottieAnimation";
import { pickRandomShareReminder } from "../lessonNotes/shareReminders";
import { Share2 } from "lucide-react";
import { openChallengeResults } from "../knowledgeChallenge/openChallengeResults";

const LeaderboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  max-width: 600px;
  margin: 0 auto;
  height: 90vh;
  box-sizing: border-box;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Logo = styled.img`
  width: 56px;
  height: 56px;
  margin-bottom: 6px;
  background-color: #222222;
  border-radius: 8px;
  padding: 6px;
`;

const Title = styled.h1`
  font-size: 18px;
  color: #333;
  margin: 4px 0 8px;
`;

const TabContainer = styled.div`
  display: flex;
  background: #f5f5f5;
  border-radius: 12px;
  margin: 15px 0;
  padding: 4px;
  gap: 3px;
`;

const Tab = styled.button`
  flex: 1;
  padding: 10px 6px;
  border: none;
  border-radius: 8px;
  font-family: "Quicksand", sans-serif;
  font-weight: bold;
  font-size: 12px;
  line-height: 1.2;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  @media (max-width: 400px) {
    font-size: 11px;
    padding: 8px 4px;
  }
  
  ${props => props.active ? `
    background: #8747d4;
    color: white;
    box-shadow: 0 2px 4px rgba(135, 71, 212, 0.3);
  ` : `
    background: transparent;
    color: #666;
    
    &:hover {
      background: rgba(135, 71, 212, 0.1);
      color: #8747d4;
    }
  `}
`;

const SubTabContainer = styled.div`
  display: flex;
  background: #f5f5f5;
  border-radius: 10px;
  margin: -6px 0 15px 0;
  padding: 3px;
  gap: 3px;
`;

const SubTab = styled.button`
  flex: 1;
  padding: 8px 12px;
  border: none;
  border-radius: 7px;
  font-family: "Quicksand", sans-serif;
  font-weight: bold;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.active ? `
    background: #8747d4;
    color: white;
    box-shadow: 0 2px 4px rgba(135, 71, 212, 0.3);
  ` : `
    background: transparent;
    color: #666;

    &:hover {
      background: rgba(135, 71, 212, 0.1);
      color: #8747d4;
    }
  `}
`;

const LeadersList = styled.div`
  flex: 1;
  background: white;
  border-radius: 15px;
  overflow-y: auto;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const LeaderItem = styled(motion.div)`
  display: flex;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
  background: ${(props) => {
    if (props.rank === 1)
      return "linear-gradient(135deg, #ffd700 0%, #fff6a8 50%, #ffd700 100%)";
    if (props.rank === 2)
      return "linear-gradient(135deg, #C0C0C0 0%, #e8e8e8 50%, #C0C0C0 100%)";
    if (props.rank === 3)
      return "linear-gradient(135deg, #CD7F32 0%, #ffa96b 50%, #CD7F32 100%)";
    return "white";
  }};

  &:last-child {
    border-bottom: none;
  }
`;

const Rank = styled.div`
  width: 40px;
  font-weight: bold;
  font-size: 24px;
  color: ${(props) => {
    if (props.rank === 1) return "#806000";
    if (props.rank === 2) return "#707070";
    if (props.rank === 3) return "#804000";
    return "#333";
  }};
`;

const UserInfo = styled.div`
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-left: 15px;
  gap: 10px;
  min-width: 0;
`;

const NameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
  overflow: visible;
`;

const Name = styled.div`
  font-weight: 600;
  font-size: clamp(12px, 4vw, 20px);
  color: ${(props) => (props.rank <= 3 ? "#000" : "#333")};
  display: flex;
  flex-direction: column;
  line-height: 1.2;

  div:first-child {
    text-transform: capitalize;
  }

  div:last-child {
    text-transform: capitalize;
  }
`;

const LongestStreakDisplay = styled.div`
  font-size: 12px;
  color: ${(props) => (props.rank <= 3 ? "#666" : "#888")};
  font-weight: 500;
  margin-top: 2px;
  font-style: italic;
`;

const LessonCount = styled.div`
  font-weight: 600;
  font-size: 20px;
  color: ${(props) => (props.rank <= 3 ? "#000" : "#666")};
  white-space: nowrap;
  flex-shrink: 0;
  padding-left: 8px;
  align-self: center;
`;

const FreezeIcon = styled.span`
  margin-left: 6px;
`;

const YouBadge = styled.div`
  background-color: #8747d4;
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: bold;
  flex-shrink: 0;
`;

const ChallengeItem = styled(motion.div)`
  display: flex;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  background: white;
  gap: 14px;

  &:last-child {
    border-bottom: none;
  }

  &:active {
    background: #f8f6ff;
  }
`;

const ChallengeMedal = styled.div`
  font-size: 28px;
  flex-shrink: 0;
`;

const ChallengeInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ChallengeName = styled.div`
  font-weight: 500;
  font-size: 0.85rem;
  color: #888;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChallengeWinnerName = styled.div`
  font-weight: 700;
  font-size: 1rem;
  color: #333;
  text-transform: capitalize;
  margin-bottom: 4px;
`;

const ChallengeArrow = styled.div`
  color: #8747d4;
  font-size: 1.8rem;
  font-weight: 700;
  flex-shrink: 0;
`;

const NoChallengesText = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #999;
  font-size: 0.95rem;
  font-weight: 500;
`;

const ShareQuoteCard = styled.div`
  text-align: center;
  padding: 14px 16px;
  margin: 0 0 12px 0;
  border-radius: 12px;
  flex-shrink: 0;
  background: ${(props) => (props.$dark ? "#262c3c" : "#fcf5e5")};
  color: ${(props) => (props.$dark ? "#f3e6c4" : "#4a3600")};
`;

const QuoteArabic = styled.p`
  margin: 0 0 6px;
  font-family: "Me Quran", serif;
  font-size: 1.15rem;
  line-height: 1.7;
  direction: rtl;
`;

const QuoteEnglish = styled.p`
  margin: 0;
  font-size: 0.92rem;
  line-height: 1.4;
  font-weight: 600;
`;

const QuoteSource = styled.p`
  margin: 4px 0 0;
  font-size: 0.75rem;
  font-weight: 700;
  opacity: 0.78;
`;

const ShareClimbHint = styled.p`
  text-align: center;
  margin: 0 0 14px;
  padding-bottom: 4px;
  font-family: "Quicksand", sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
  color: ${(props) => (props.$dark ? "white" : "#333")};
`;

const shareShine = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const ShareButtonLookalike = styled.span`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 3px;
  vertical-align: middle;
  margin: 0 4px;
  color: #4a3600;
  background: linear-gradient(135deg, #ffd700 0%, #fff6a8 50%, #ffd700 100%);
  background-size: 200% auto;
  animation: ${shareShine} 3s linear infinite;
  box-shadow: 0 2px 0 #b8860b, 0 2px 8px rgba(255, 215, 0, 0.45);
  font-size: 0.62rem;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  font-family: "Quicksand", sans-serif;
  font-weight: bold;
  padding: 0.18rem 0.4rem;
  border-radius: 8px;
  pointer-events: none;
  user-select: none;
  position: relative;
  top: -1px;
`;

const ShineEffect = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.5),
    transparent
  );
  pointer-events: none;
`;

const beatAnimation = {
  scale: [1, 1.02, 1],
  transition: {
    duration: 1.2,
    repeat: Infinity,
    ease: "easeInOut",
    times: [0, 0.5, 1],
  },
};

const entryAnimation = {
  opacity: 1,
  y: 0,
};

// Define a styled button similar to SolidButton
const StyledButton = styled.button`
  padding: 0.55rem 1.25rem;
  border-radius: 12px;
  font-size: 0.82rem;
  font-weight: bold;
  cursor: pointer;
  font-family: "Quicksand", sans-serif;
  width: auto;
  min-width: 180px;
  max-width: 90%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: transform 0.01s ease;
  background: #8747d4;
  color: white;
  border: none;
  box-shadow: 0 3px 0 #6535a3;
  position: relative;
  top: 0;

  &:hover {
    background: #9859e0;
  }

  &:active {
    top: 3px;
    box-shadow: 0 0 0 #6535a3;
  }

  @media (max-width: 768px) {
    width: auto;
  }
`;

const calculateRanks = (data) => {
  let currentRank = 0;
  let previousScore = null;

  return data.map((user) => {
    if (user.completedLessons !== previousScore) {
      currentRank++;
    }
    previousScore = user.completedLessons;
    return { ...user, rank: currentRank };
  });
};

const calculateStreakRanks = (data, scoreKey) => {
  let currentRank = 0;
  let previousScore = null;

  return data.map((user) => {
    if (user[scoreKey] !== previousScore) {
      currentRank++;
    }
    previousScore = user[scoreKey];
    return { ...user, rank: currentRank };
  });
};

// Add this styled component with the other styled components
const LottieContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100vw;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  background-color: #fff;

  > div {
    width: 100%;
    max-width: 300px;

    @media only screen and (min-width: 601px) {
      max-width: 50%;
    }
  }
`;

function Leaderboard() {
  const { isDarkMode } = useAmazonContext();
  const history = useHistory();
  const [activeTab, setActiveTab] = useState('lessons');
  const [streakSubTab, setStreakSubTab] = useState('highest');
  // Seed from cache so returning to this tab paints instantly instead of showing the
  // loading animation again while the same data is refetched.
  const cachedLessons = readCache("/leaderboard");
  const [leaderboardData, setLeaderboardData] = useState(() =>
    cachedLessons ? calculateRanks(cachedLessons.data) : []
  );
  const [streakLeaderboardData, setStreakLeaderboardData] = useState(
    () => readCache("/getStreakLeaderboard")?.data || []
  );
  const [shareLeaderboardData, setShareLeaderboardData] = useState(
    () => readCache("/shareLeaderboard")?.data || []
  );
  const [challengeWinners, setChallengeWinners] = useState(() =>
    (readCache("/knowledge-challenge/all")?.data || []).filter(
      (c) => c.winner && c.winner.userEmail
    )
  );
  const [isLoading, setIsLoading] = useState(!cachedLessons);
  const currentUserEmail = localStorage.getItem("userEmail");
  const userRefs = useRef([]);
  const [shareReminder, setShareReminder] = useState(() =>
    pickRandomShareReminder()
  );

  useEffect(() => {
    if (activeTab !== "shares") return;
    setShareReminder((prev) => pickRandomShareReminder(prev));
  }, [activeTab]);

  useEffect(() => {
    let cancelled = false;

    // The lessons board is the tab shown on open, so it is fetched and painted on its own.
    // The other three boards load right behind it instead of holding up the first paint.
    cachedGet("/leaderboard")
      .then((data) => {
        if (cancelled) return;
        setLeaderboardData(calculateRanks(data));
      })
      .catch((error) => {
        console.error("Error fetching leaderboard data:", error);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    cachedGet("/getStreakLeaderboard")
      .then((data) => {
        if (!cancelled) setStreakLeaderboardData(data || []);
      })
      .catch(() => {});

    cachedGet("/shareLeaderboard")
      .then((data) => {
        if (!cancelled) setShareLeaderboardData(data || []);
      })
      .catch(() => {});

    cachedGet("/knowledge-challenge/all")
      .then((data) => {
        if (cancelled) return;
        setChallengeWinners(
          (data || []).filter((c) => c.winner && c.winner.userEmail)
        );
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  const highestStreakData = useMemo(
    () =>
      calculateStreakRanks(
        [...streakLeaderboardData]
          .filter((user) => user.longestStreak > 0)
          .sort((a, b) => b.longestStreak - a.longestStreak),
        "longestStreak"
      ),
    [streakLeaderboardData]
  );

  const activeStreakData = useMemo(
    () =>
      calculateStreakRanks(
        [...streakLeaderboardData]
          .filter((user) => user.currentStreak > 0)
          .sort((a, b) => b.currentStreak - a.currentStreak),
        "currentStreak"
      ),
    [streakLeaderboardData]
  );

  const rankedShareData = useMemo(
    () =>
      calculateStreakRanks(
        [...shareLeaderboardData]
          .filter((user) => user.shareCount > 0)
          .sort((a, b) => b.shareCount - a.shareCount),
        "shareCount"
      ),
    [shareLeaderboardData]
  );

  const currentStreakData =
    streakSubTab === "highest" ? highestStreakData : activeStreakData;
  const currentData =
    activeTab === "lessons"
      ? leaderboardData
      : activeTab === "streaks"
        ? currentStreakData
        : activeTab === "shares"
          ? rankedShareData
          : [];
  const isHighestStreaks = activeTab === "streaks" && streakSubTab === "highest";
  const isActiveStreaks = activeTab === "streaks" && streakSubTab === "active";

  const scrollToCurrentUser = () => {
    const currentUserIndex = currentData.findIndex(
      (user) => user.userEmail === currentUserEmail
    );
    if (currentUserIndex !== -1 && userRefs.current[currentUserIndex]) {
      userRefs.current[currentUserIndex].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  if (isLoading) {
    return (
      <div>
        <LottieContainer>
          <LottieAnimation
            lottieFile="https://hhcourses-assets.s3.us-east-2.amazonaws.com/General/LottieFiles/loading.json"
            loop={true}
          ></LottieAnimation>
        </LottieContainer>
        <BottomNavBar></BottomNavBar>
      </div>
    );
  }

  return (
    <LeaderboardContainer>
      <Header>
        <Logo src={HHLogo} alt="Quranic" />
        <Title style={{ color: isDarkMode ? "white" : "#333" }}>
          Leaderboard
        </Title>
        {activeTab !== 'challenges' && (
          <StyledButton onClick={scrollToCurrentUser}>
            GO TO MY POSITION
          </StyledButton>
        )}
      </Header>

      <TabContainer>
        <Tab active={activeTab === 'lessons'} onClick={() => setActiveTab('lessons')}>Most Lessons</Tab>
        <Tab active={activeTab === 'streaks'} onClick={() => setActiveTab('streaks')}>Streaks</Tab>
        <Tab active={activeTab === 'shares'} onClick={() => setActiveTab('shares')}>Most Shares</Tab>
        {challengeWinners.length > 0 && (
          <Tab active={activeTab === 'challenges'} onClick={() => setActiveTab('challenges')}>Challenge Winners</Tab>
        )}
      </TabContainer>

      {activeTab === 'streaks' && (
        <SubTabContainer>
          <SubTab
            active={streakSubTab === 'highest'}
            onClick={() => setStreakSubTab('highest')}
          >
            Highest Streaks
          </SubTab>
          <SubTab
            active={streakSubTab === 'active'}
            onClick={() => setStreakSubTab('active')}
          >
            Active Streaks
          </SubTab>
        </SubTabContainer>
      )}

      {activeTab === 'shares' && shareReminder && (
        <>
          <ShareQuoteCard $dark={isDarkMode}>
            {shareReminder.arabic ? (
              <QuoteArabic>{shareReminder.arabic}</QuoteArabic>
            ) : null}
            <QuoteEnglish>{shareReminder.english}</QuoteEnglish>
            <QuoteSource>{shareReminder.source}</QuoteSource>
          </ShareQuoteCard>
          <ShareClimbHint $dark={isDarkMode}>
            Climb to the top by hitting
            <ShareButtonLookalike>
              <Share2 size={11} strokeWidth={2.5} />
              share
            </ShareButtonLookalike>
            in lessons.
          </ShareClimbHint>
        </>
      )}

      <LeadersList>
        {activeTab === 'challenges' ? (
          challengeWinners.length === 0 ? (
            <NoChallengesText>No challenge winners yet</NoChallengesText>
          ) : (
            challengeWinners.map((challenge) => (
              <ChallengeItem
                key={challenge._id}
                onClick={() => openChallengeResults(history, challenge)}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                whileTap={{ scale: 0.98 }}
              >
                <ChallengeMedal>🏆</ChallengeMedal>
                <ChallengeInfo>
                  <ChallengeWinnerName>
                    {challenge.winner.givenName} {challenge.winner.familyName}
                  </ChallengeWinnerName>
                  <ChallengeName>{challenge.name || "Knowledge Challenge"}</ChallengeName>
                </ChallengeInfo>
                <ChallengeArrow>›</ChallengeArrow>
              </ChallengeItem>
            ))
          )
        ) : activeTab === 'shares' && rankedShareData.length === 0 ? (
          <NoChallengesText>Share a lesson to appear here</NoChallengesText>
        ) : (
          currentData.map((user, index) => {
            const isCurrentUser = user.userEmail === currentUserEmail;
            return (
              <LeaderItem
                key={user.userEmail}
                rank={user.rank}
                ref={(el) => (userRefs.current[index] = el)}
                initial={{ opacity: 0 }}
                animate={{
                  opacity: 1,
                  scale: isCurrentUser ? beatAnimation.scale : 1,
                }}
                transition={{
                  duration: 0.3,
                  scale: isCurrentUser
                    ? {
                        duration: 1.2,
                        repeat: Infinity,
                        ease: "easeInOut",
                        times: [0, 0.5, 1],
                      }
                    : undefined,
                }}
                style={{
                  position: "relative",
                  overflow: "hidden",
                  boxShadow: isCurrentUser
                    ? "0 0 10px rgba(135, 71, 212, 0.3)"
                    : "none",
                  transformOrigin: "center center",
                }}
              >
                <Rank rank={user.rank}>{user.rank}</Rank>
                <UserInfo>
                  <NameContainer>
                    <div>
                      <Name rank={user.rank}>
                        <div>{user.givenName || 'Anonymous'}</div>
                        <div>{user.familyName || 'User'}</div>
                      </Name>
                      {isActiveStreaks && user.longestStreak > 0 && (
                        <LongestStreakDisplay rank={user.rank}>
                          Longest: {user.longestStreak} {user.longestStreak === 1 ? 'day' : 'days'}
                        </LongestStreakDisplay>
                      )}
                      {isHighestStreaks && user.currentStreak > 0 && (
                        <LongestStreakDisplay rank={user.rank}>
                          Active: {user.currentStreak} {user.currentStreak === 1 ? 'day' : 'days'}
                        </LongestStreakDisplay>
                      )}
                    </div>
                    {isCurrentUser && <YouBadge>YOU</YouBadge>}
                  </NameContainer>
                  <LessonCount rank={user.rank}>
                    {activeTab === 'lessons'
                      ? `${user.completedLessons} Lessons`
                      : activeTab === 'shares'
                        ? `${user.shareCount} ${user.shareCount === 1 ? 'Share' : 'Shares'}`
                      : isHighestStreaks
                        ? `${user.longestStreak} Day Streak`
                        : `${user.currentStreak} Day Streak`
                    }
                    {isActiveStreaks && (user.streakFreezeActive || user.isFrozen) && (
                      <FreezeIcon title="Streak frozen">❄️</FreezeIcon>
                    )}
                  </LessonCount>
                </UserInfo>
                {index < 3 && (
                  <ShineEffect
                    initial={{ x: "-100%" }}
                    animate={{ x: "100%" }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      repeatDelay: 3,
                      ease: "easeInOut",
                    }}
                  />
                )}
              </LeaderItem>
            );
          })
        )}
      </LeadersList>
      <BottomNavBar />
    </LeaderboardContainer>
  );
}

export default Leaderboard;
